import { Component, Input } from '@angular/core';
import { ResumeTemplate, SectionDef } from './resume-templates';

/** Live résumé preview — also what gets printed / saved as PDF. */
@Component({
  selector: 'app-resume-preview',
  template: `
  <div class="resume-paper" [ngClass]="'style-' + (styleName || 'classic')">
    <div class="watermark" *ngIf="watermark">TEST DRIVE</div>
    <header class="r-head">
      <h1>{{ p.fullName || 'Your Name' }}</h1>
      <div class="r-headline" *ngIf="p.headline">{{ p.headline }}</div>
      <div class="r-contact">
        <span *ngIf="p.email">{{ p.email }}</span>
        <span *ngIf="p.phone">{{ p.phone }}</span>
        <span *ngIf="p.location">{{ p.location }}</span>
        <span *ngIf="p.website">{{ p.website }}</span>
      </div>
    </header>

    <ng-container *ngFor="let s of body()">
      <section class="r-sec" *ngIf="hasContent(s)">
        <h2>{{ s.title }}</h2>

        <!-- key/value sections -->
        <ng-container *ngIf="s.kind === 'fields'">
          <p *ngIf="isTextOnly(s)" class="r-text">{{ content[s.key].text }}</p>
          <dl *ngIf="!isTextOnly(s)" class="r-kv">
            <ng-container *ngFor="let f of s.fields">
              <ng-container *ngIf="content[s.key][f.key]">
                <dt>{{ f.label }}</dt><dd>{{ content[s.key][f.key] }}</dd>
              </ng-container>
            </ng-container>
          </dl>
        </ng-container>

        <!-- tags -->
        <div *ngIf="s.kind === 'tags'" class="r-tags"><span *ngFor="let t of content[s.key]">{{ t }}</span></div>

        <!-- repeatable items -->
        <ng-container *ngIf="s.kind === 'list'">
          <div class="r-item" *ngFor="let it of content[s.key]">
            <div class="r-item-head">
              <b>{{ primary(s, it) }}</b>
              <span class="r-dates" *ngIf="dates(it)">{{ dates(it) }}</span>
            </div>
            <div class="r-sub" *ngIf="secondary(s, it)">{{ secondary(s, it) }}</div>
            <ng-container *ngFor="let f of s.fields">
              <ng-container *ngIf="f.type === 'textarea' && it[f.key]">
                <div class="r-sub-label" *ngIf="longFieldCount(s) > 1">{{ f.label }}</div>
                <ul class="r-bullets"><li *ngFor="let line of lines(it[f.key])">{{ line }}</li></ul>
              </ng-container>
            </ng-container>
          </div>
        </ng-container>
      </section>
    </ng-container>
  </div>`
})
export class ResumePreviewComponent {
  @Input() template: ResumeTemplate;
  @Input() content: any = {};
  @Input() styleName = 'classic';
  @Input() watermark = false;

  get p(): any { return (this.content && this.content.personal) || {}; }

  body(): SectionDef[] { return this.template ? this.template.sections.filter(s => s.key !== 'personal') : []; }

  hasContent(s: SectionDef): boolean {
    const v = this.content && this.content[s.key];
    if (!v) { return false; }
    if (Array.isArray(v)) { return v.length > 0; }
    return Object.keys(v).some(k => !!v[k]);
  }

  isTextOnly(s: SectionDef) { return s.fields && s.fields.length === 1 && s.fields[0].key === 'text'; }

  /** Short (non-textarea, non-date) fields; first is the bold line, the rest go on the sub line. */
  private shortFields(s: SectionDef) {
    return s.fields.filter(f => f.type !== 'textarea' && f.type !== 'month' && f.key !== 'start' && f.key !== 'end');
  }
  primary(s: SectionDef, it: any) {
    const f = this.shortFields(s);
    return f.length ? it[f[0].key] : '';
  }
  secondary(s: SectionDef, it: any) {
    return this.shortFields(s).slice(1)
      .map(f => it[f.key] ? (f.type === 'number' || f.key === 'mayContact' ? f.label + ': ' + it[f.key] : it[f.key]) : '')
      .filter(Boolean).join(' · ');
  }
  longFieldCount(s: SectionDef) { return s.fields.filter(f => f.type === 'textarea').length; }

  dates(it: any) {
    if (!it.start && !it.end) { return ''; }
    return (this.month(it.start) || '') + ' – ' + (it.end ? this.month(it.end) : 'Present');
  }
  private month(v: string) {
    if (!v) { return ''; }
    const [y, m] = v.split('-');
    const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return m ? names[+m - 1] + ' ' + y : y;
  }
  lines(t: string) { return String(t).split('\n').map(x => x.replace(/^[-•*]\s*/, '').trim()).filter(Boolean); }
}
